import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { BRANCHES, JOBS_BY_BRANCH } from "../data/content";
import { useAuth } from "../context/AuthContext";
import PayPalCheckout from "../components/PayPalCheckout";
import {
  ShieldStar, CheckCircle, Lock, HandHeart, Receipt, X, Plus,
  ArrowRight, Sparkle, Info, CurrencyDollar, LockKey, Eye,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import ReactMarkdown from "react-markdown";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PRICE = 5;
const LIST_PRICE = 10;

export default function CareerDeepDive({ onOpenAuth }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const initialBranch = (() => {
    const p = new URLSearchParams(window.location.search).get("branch");
    return p && ["army","navy","air-force","marines","coast-guard","space-force"].includes(p) ? p : "";
  })();
  const [branch, setBranch] = useState(initialBranch);
  const [jobs, setJobs] = useState([]);
  const [custom, setCustom] = useState("");
  const [busy, setBusy] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const canceled = new URLSearchParams(window.location.search).get("canceled") === "1";

  const toggleJob = (j) => {
    setJobs((prev) =>
      prev.includes(j)
        ? prev.filter((x) => x !== j)
        : prev.length >= 3
        ? prev
        : [...prev, j]
    );
  };

  const addCustom = () => {
    const v = custom.trim();
    if (!v) return;
    if (jobs.length >= 3) {
      toast("Up to 3 jobs", { description: "Remove one to add another." });
      return;
    }
    setJobs((prev) => [...prev, v]);
    setCustom("");
  };

  const startCheckout = async () => {
    if (!user) {
      onOpenAuth?.("register");
      toast("Please log in to continue", { description: "Your report will be tied to your account." });
      return;
    }
    if (!branch) { toast.error("Choose a branch first"); return; }
    if (jobs.length === 0) { toast.error("Pick at least one job"); return; }

    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/career-deepdive/checkout`, {
        branch,
        jobs,
        origin_url: window.location.origin,
      });
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
    } finally {
      setBusy(false);
    }
  };

  const generatePreview = async () => {
    if (!user) {
      onOpenAuth?.("register");
      toast("Log in to try the free preview", { description: "Free for logged-in members." });
      return;
    }
    if (!branch) { toast.error("Choose a branch first"); return; }
    if (jobs.length === 0) { toast.error("Pick at least one job"); return; }
    setPreviewLoading(true);
    setPreview(null);
    try {
      const { data } = await axios.post(`${API}/career-deepdive/preview`, { branch, jobs });
      setPreview(data);
      // Scroll to preview after a beat
      setTimeout(() => {
        document.getElementById("preview-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not generate preview");
    } finally {
      setPreviewLoading(false);
    }
  };

  const branchJobs = branch ? (JOBS_BY_BRANCH[branch] || []) : [];

  return (
    <main className="max-w-6xl mx-auto px-6 md:px-10 py-14 md:py-20">
      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <span className="inline-flex items-center gap-2 bg-[#0033A0]/10 text-[#0033A0] px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
            <Sparkle size={14} weight="fill" /> Premium tool
          </span>
          <h1 className="font-display text-4xl md:text-6xl font-black tracking-tighter text-slate-900 mt-4 leading-[1.05]">
            Career Deep Dive
          </h1>
          <p className="text-lg text-slate-700 mt-5 max-w-2xl leading-relaxed">
            Pick a branch and up to three jobs. We{`'`}ll generate a tailored report covering daily duties, training pipeline, certifications, civilian career paths, and how your benefits stack up.
          </p>
        </div>
        <div className="bg-white border-2 border-[#D90429] rounded-2xl p-6 md:min-w-[280px] shrink-0 relative" data-testid="price-card">
          <span className="absolute -top-3 -right-3 bg-[#D90429] text-white text-xs font-black uppercase tracking-wider px-3 py-1.5 rounded-full shadow-md" data-testid="discount-badge">
            90% OFF
          </span>
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Launch price · One-time</div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="font-display text-5xl font-black text-slate-900">$1</span>
            <span className="text-slate-500 text-sm">USD</span>
            <span className="text-slate-400 text-lg line-through ml-2" data-testid="list-price">$10</span>
          </div>
          <p className="text-xs text-slate-500 mt-2 leading-relaxed">Covers up to 3 jobs in a single branch. <strong className="text-[#D90429]">Try job #1 free</strong> before you pay.</p>
        </div>
      </div>

      {canceled && (
        <div className="mt-6 bg-amber-50 border border-amber-200 text-amber-900 rounded-md px-4 py-3 text-sm" data-testid="checkout-canceled-banner">
          Checkout canceled — your card was not charged.
        </div>
      )}

      {/* TRUST STRIP */}
      <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3">
        <Trust icon={Lock} label="Secure PayPal checkout" />
        <Trust icon={ShieldStar} label="Branch-specific & honest" />
        <Trust icon={HandHeart} label="50% of net profit donated" />
        <Trust icon={Receipt} label="Instant delivery & receipt" />
      </div>

      {/* BUILDER */}
      <section className="mt-14 grid grid-cols-1 lg:grid-cols-5 gap-8">
        <div className="lg:col-span-3 space-y-8">
          {/* Step 1: branch */}
          <div data-testid="deepdive-step-branch">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#0033A0]">Step 1</p>
            <h2 className="font-display text-2xl font-bold text-slate-900 mt-2">Pick a branch</h2>
            <div className="mt-5 grid grid-cols-2 md:grid-cols-3 gap-3">
              {BRANCHES.map((b) => {
                const active = branch === b.slug;
                return (
                  <button
                    key={b.slug}
                    onClick={() => { setBranch(b.slug); setJobs([]); }}
                    data-testid={`deepdive-branch-${b.slug}`}
                    className={`text-left p-4 rounded-xl border-2 transition-all ${
                      active
                        ? "border-[#0033A0] bg-[#0033A0]/5"
                        : "border-slate-200 bg-white hover:border-slate-400"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-display font-bold text-slate-900">{b.name}</span>
                      {active && <CheckCircle size={18} weight="fill" className="text-[#0033A0]" />}
                    </div>
                    <div className="h-1 w-12 rounded-full mt-2" style={{ background: b.color }} />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Step 2: jobs */}
          <div data-testid="deepdive-step-jobs">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#0033A0]">Step 2</p>
            <h2 className="font-display text-2xl font-bold text-slate-900 mt-2">
              Choose up to 3 jobs <span className="text-slate-400 text-base font-normal">({jobs.length}/3)</span>
            </h2>

            {!branch ? (
              <p className="text-slate-500 mt-3 text-sm">Pick a branch above to see job options.</p>
            ) : (
              <>
                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {branchJobs.map((j) => {
                    const active = jobs.includes(j);
                    const disabled = !active && jobs.length >= 3;
                    return (
                      <button
                        key={j}
                        onClick={() => toggleJob(j)}
                        disabled={disabled}
                        data-testid={`deepdive-job-${j.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                        className={`text-left text-sm font-medium p-3 rounded-md border transition-all flex items-center justify-between gap-2 ${
                          active
                            ? "border-[#0033A0] bg-[#0033A0] text-white"
                            : disabled
                            ? "border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed"
                            : "border-slate-300 bg-white text-slate-800 hover:border-slate-500"
                        }`}
                      >
                        <span>{j}</span>
                        {active && <CheckCircle size={16} weight="fill" />}
                      </button>
                    );
                  })}
                </div>

                <div className="mt-5">
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Or add a custom job</label>
                  <div className="mt-2 flex gap-2">
                    <input
                      value={custom}
                      onChange={(e) => setCustom(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustom())}
                      placeholder="e.g., Combat Photographer, Linguist"
                      data-testid="deepdive-custom-job-input"
                      className="flex-1 px-3 py-2.5 rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#0033A0]"
                    />
                    <button
                      onClick={addCustom}
                      data-testid="deepdive-add-custom-btn"
                      className="inline-flex items-center gap-1 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold px-4 rounded-md"
                    >
                      <Plus size={16} weight="bold" /> Add
                    </button>
                  </div>
                </div>

                {jobs.length > 0 && (
                  <div className="mt-5 flex flex-wrap gap-2" data-testid="deepdive-selected-jobs">
                    {jobs.map((j) => (
                      <span key={j} className="inline-flex items-center gap-1.5 bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 rounded-full">
                        {j}
                        <button onClick={() => setJobs(jobs.filter((x) => x !== j))} aria-label={`Remove ${j}`}>
                          <X size={12} weight="bold" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* SUMMARY / CHECKOUT */}
        <aside className="lg:col-span-2">
          <div className="bg-white border border-slate-200 rounded-2xl p-6 lg:sticky lg:top-24">
            <h3 className="font-display font-bold text-xl text-slate-900">Your report</h3>
            <p className="text-sm text-slate-500 mt-1">Generated immediately after payment.</p>

            <dl className="mt-5 space-y-3 text-sm">
              <div className="flex justify-between gap-4">
                <dt className="text-slate-500">Branch</dt>
                <dd className="font-semibold text-slate-900 text-right" data-testid="summary-branch">
                  {branch ? BRANCHES.find((b) => b.slug === branch)?.name : "—"}
                </dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-slate-500">Jobs</dt>
                <dd className="font-semibold text-slate-900 text-right" data-testid="summary-jobs">{jobs.length || "—"}</dd>
              </div>
            </dl>

            <div className="border-t border-slate-200 my-5" />

            {!user && (
              <div className="bg-amber-50 border-2 border-amber-300 rounded-md p-3 mb-4 text-center" data-testid="login-required-banner">
                <LockKey size={20} weight="duotone" className="text-amber-700 mx-auto mb-1" />
                <p className="text-sm font-semibold text-amber-900">Log in to purchase</p>
                <p className="text-xs text-amber-800 mt-1 leading-relaxed">Your report stays in your account forever. Buy once, view anytime.</p>
                <button
                  onClick={() => onOpenAuth?.("register")}
                  data-testid="banner-login-btn"
                  className="mt-2 w-full bg-amber-700 hover:bg-amber-800 text-white text-sm font-semibold py-2 px-3 rounded-md transition-colors"
                >
                  Log in or sign up
                </button>
              </div>
            )}

            <dl className="space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-slate-600">Career Deep Dive</dt><dd className="text-slate-900"><span className="text-slate-400 line-through mr-1.5">$10.00</span>$1.00</dd></div>
              <div className="flex justify-between text-emerald-600 font-semibold"><dt>Launch discount</dt><dd>−90%</dd></div>
              <div className="flex justify-between text-slate-500"><dt>Processing fees</dt><dd>Included</dd></div>
              <div className="flex justify-between font-display font-bold text-slate-900 text-lg pt-2 border-t border-slate-100"><dt>Total</dt><dd data-testid="summary-total">$1.00 USD</dd></div>
            </dl>

            <button
              type="button"
              onClick={generatePreview}
              disabled={previewLoading || !user || !branch || jobs.length === 0}
              data-testid="generate-preview-btn"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-white hover:bg-slate-50 border-2 border-[#0033A0] text-[#0033A0] font-semibold py-3 px-6 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Eye size={16} weight="bold" />
              {previewLoading ? "Generating preview…" : "Try job #1 free"}
            </button>

            <p className="text-xs text-slate-500 mt-5 mb-2 text-center">Or unlock all {jobs.length || 3} jobs now — pay with PayPal or any card.</p>

            <PayPalCheckout
              purpose="deepdive"
              disabled={!user || !branch || jobs.length === 0 || busy}
              getPayload={async () => {
                if (!user) { onOpenAuth?.("register"); throw new Error("login required"); }
                return { branch, jobs };
              }}
              onPaid={(res) => {
                if (res?.paid && res?.report_id) {
                  toast.success("Payment confirmed!");
                  navigate(`/career-deepdive/reports/${res.report_id}`);
                } else {
                  toast.error("Payment not completed yet — try refreshing your dashboard in a moment.");
                }
              }}
              onError={(err) => {
                if (err?.canceled) return;
                toast.error("PayPal payment failed or was canceled");
              }}
            />

            {!user && (
              <button
                onClick={() => onOpenAuth?.("register")}
                data-testid="deepdive-login-prompt"
                className="mt-3 w-full text-center text-sm text-[#0033A0] hover:text-[#002277] font-semibold"
              >
                Log in to checkout →
              </button>
            )}

            <div className="mt-4 flex items-start gap-2 bg-emerald-50 border border-emerald-200 rounded-md p-3 text-xs text-emerald-900">
              <CheckCircle size={16} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" />
              <span><strong>Buy once, view forever.</strong> Your report is saved to your account — return anytime from your dashboard.</span>
            </div>

            <p className="text-[11px] text-slate-500 text-center mt-3 leading-relaxed">
              Securely processed by PayPal. We never see your card details. By continuing you agree to our digital-goods refund terms below.
            </p>
          </div>
        </aside>
      </section>

      {/* PREVIEW SECTION */}
      {(preview || previewLoading) && (
        <section id="preview-section" className="mt-16" data-testid="preview-section">
          <div className="flex items-center gap-2">
            <Eye size={18} weight="duotone" className="text-emerald-600" />
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-emerald-700">Free preview</span>
          </div>
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mt-3">
            {preview?.first_job || "Generating your preview…"}
          </h2>
          {preview && (
            <p className="text-slate-600 mt-2 text-sm">
              {preview.branch_display} · This is 1 of {preview.all_jobs?.length || jobs.length} jobs you selected.
            </p>
          )}

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            {/* Preview content */}
            <article className="lg:col-span-7 bg-white border border-slate-200 rounded-2xl p-6 md:p-8 prose prose-slate max-w-none prose-headings:font-display prose-h2:text-xl prose-h2:mt-6 prose-h2:mb-2 prose-li:my-0.5" data-testid="preview-markdown">
              {previewLoading && !preview ? (
                <p className="text-slate-500">Boot Buddy is writing your preview…</p>
              ) : (
                <ReactMarkdown>{preview.content_markdown}</ReactMarkdown>
              )}
            </article>

            {/* Paywall card */}
            <aside className="lg:col-span-5">
              <div className="bg-[#0A1128] text-white rounded-2xl p-6 md:p-7 relative overflow-hidden">
                <span className="absolute -top-3 -right-3 bg-[#D90429] text-white text-xs font-black uppercase tracking-wider px-3 py-1.5 rounded-full shadow-md">50% OFF</span>
                <LockKey size={28} weight="duotone" className="text-[#FCA5A5]" />
                <h3 className="font-display text-2xl font-bold mt-3 leading-tight">
                  Unlock the full Career Deep Dive
                </h3>
                <p className="text-slate-300 text-sm mt-3 leading-relaxed">
                  Pay once to unlock <strong className="text-white">all {preview?.all_jobs?.length || jobs.length} jobs</strong> with training pipeline, certifications, civilian career paths, GI Bill / TSP / SkillBridge alignment, and bottom-line guidance for each.
                </p>

                <ul className="mt-5 space-y-2 text-sm text-slate-200">
                  <li className="flex items-center gap-2"><CheckCircle size={16} weight="fill" className="text-emerald-400" /> Full report for each selected job</li>
                  <li className="flex items-center gap-2"><CheckCircle size={16} weight="fill" className="text-emerald-400" /> Print, download, return to anytime</li>
                  <li className="flex items-center gap-2"><CheckCircle size={16} weight="fill" className="text-emerald-400" /> 50% of net profit donated to veterans</li>
                </ul>

                <div className="mt-5 flex items-baseline gap-2">
                  <span className="font-display font-black text-4xl">$1</span>
                  <span className="text-slate-400 line-through text-lg">$10</span>
                  <span className="text-xs text-slate-300">one-time</span>
                </div>

                <div className="mt-5 bg-white rounded-md p-2" data-testid="preview-paywall-buttons">
                  <PayPalCheckout
                    purpose="deepdive"
                    disabled={!user || !branch || jobs.length === 0}
                    getPayload={async () => {
                      if (!user) { onOpenAuth?.("register"); throw new Error("login required"); }
                      return { branch, jobs };
                    }}
                    onPaid={(res) => {
                      if (res?.paid && res?.report_id) {
                        toast.success("Unlocked!");
                        navigate(`/career-deepdive/reports/${res.report_id}`);
                      } else {
                        toast.error("Payment not completed yet — refresh dashboard in a moment.");
                      }
                    }}
                    onError={(err) => {
                      if (err?.canceled) return;
                      toast.error("Payment failed or canceled");
                    }}
                  />
                </div>
                <p className="text-[11px] text-slate-400 text-center mt-3 leading-relaxed">
                  Secure PayPal checkout. Pay with PayPal balance or any credit/debit card — no PayPal account required.
                </p>
              </div>
            </aside>
          </div>
        </section>
      )}

      {/* TRANSPARENCY */}
      <section className="mt-20" data-testid="donation-transparency">
        <div className="bg-[#0A1128] text-white rounded-2xl p-8 md:p-12 grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-7">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#FCA5A5]">Where the money goes</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold mt-3 leading-tight">
              50% of net profit supports veterans.
            </h2>
            <p className="text-slate-300 mt-4 leading-relaxed max-w-xl text-sm">
              For every $1 report sold, we keep just enough to cover hosting, AI generation, and PayPal processing. Half of what{`'`}s left after those real costs is donated quarterly to vetted veteran-support organizations. We post a public donation summary on a rolling basis — no specific charity is named here to avoid implying any unverified partnership.
            </p>
            <div className="mt-5 flex items-start gap-2 text-xs text-slate-400 leading-relaxed max-w-xl">
              <Info size={16} className="shrink-0 mt-0.5" />
              <span>
                {`"Net profit"`} = revenue minus PayPal fees, AI generation costs, and direct hosting attributable to this feature. It is not the same as the $1 you paid; payment processors and infrastructure consume part of every transaction.
              </span>
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="bg-white/5 border border-white/10 rounded-xl p-5">
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-300">Simple breakdown (per $1 sale)</h4>
              <ul className="mt-4 space-y-3 text-sm">
                <li className="flex justify-between items-baseline gap-4">
                  <span className="text-slate-300">PayPal processing fees</span>
                  <span className="font-mono text-white">~$0.52</span>
                </li>
                <li className="flex justify-between items-baseline gap-4">
                  <span className="text-slate-300">AI report + infrastructure</span>
                  <span className="font-mono text-white">~$0.30</span>
                </li>
                <li className="flex justify-between items-baseline gap-4 border-t border-white/10 pt-3">
                  <span className="text-slate-300">Donated to veteran nonprofits</span>
                  <span className="font-mono text-[#FCA5A5] font-bold">~50% of remainder</span>
                </li>
                <li className="flex justify-between items-baseline gap-4">
                  <span className="text-slate-300">Boot To Benefit operations</span>
                  <span className="font-mono text-white">~50% of remainder</span>
                </li>
              </ul>
              <p className="text-[10px] text-slate-400 mt-4 leading-relaxed">
                Exact amounts vary slightly with PayPal{`'`}s effective rate and AI usage. Figures are estimates, not a promise of a specific dollar amount per sale.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* TERMS */}
      <section className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 text-sm" data-testid="terms-section">
        <Term icon={CurrencyDollar} title="Payment terms">
          A single $1.00 USD charge (90% launch discount off our $10 list price). Processed by PayPal (PayPal balance or credit/debit card accepted). No subscription, no recurring billing, no stored card on our side.
        </Term>
        <Term icon={Receipt} title="Refund policy">
          Because the report is delivered to you immediately upon payment, all sales are final. If something goes wrong during generation, contact <a href="mailto:boottobenefit.contact@gmail.com" className="text-[#0033A0] font-semibold hover:underline">boottobenefit.contact@gmail.com</a> and we{`'`}ll regenerate or refund.
        </Term>
        <Term icon={ShieldStar} title="Donation precision">
          We donate <strong>50% of net profit</strong>, not 50% of revenue. {`"Net profit"`} is calculated after PayPal fees, AI costs, and direct infrastructure. We publish updates rather than per-sale promises.
        </Term>
      </section>
    </main>
  );
}

function Trust({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-md px-3 py-2.5">
      <Icon size={18} weight="duotone" className="text-[#0033A0] shrink-0" />
      <span className="text-xs font-semibold text-slate-700">{label}</span>
    </div>
  );
}

function Term({ icon: Icon, title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5">
      <Icon size={20} weight="duotone" className="text-[#0033A0]" />
      <h4 className="font-display font-bold text-slate-900 mt-2">{title}</h4>
      <p className="text-slate-600 mt-2 leading-relaxed">{children}</p>
    </div>
  );
}
